import { clinicalNetworkRouteId, assertClinicalChartReady, saveClinicalResource, readClinicalResourceList } from './clinicalMutationAdapter';
import { requireClinicalSaveResult } from '../clinicalSaveResult';
import config from '../../config';
import { getAuthHeaders, handleResponse, handleNoContentResponse } from './common';

const visitApi = {
    // Pregnancy APIs
    fetchPregnancies: async (referenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/pregnancies`, {
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    addPregnancy: async (referenceId, pregnancyData) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/pregnancies`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(pregnancyData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    updatePregnancy: async (pregnancyReferenceId, pregnancyData) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(pregnancyData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    deletePregnancy: async (pregnancyReferenceId, confirmation) => {
        const res = await fetch(
            `${config.api.url}/pregnancies/${pregnancyReferenceId}?confirmation=${encodeURIComponent(confirmation || '')}`,
            { method: 'DELETE', headers: getAuthHeaders() }
        );
        return requireClinicalSaveResult(await handleNoContentResponse(res));
    },

    previewDuplicatePregnancyResolution: async (pregnancyReferenceId) => {
        const res = await fetch(
            `${config.api.url}/pregnancies/${pregnancyReferenceId}/duplicate-resolution`,
            { headers: getAuthHeaders() }
        );
        return requireClinicalSaveResult(await handleResponse(res));
    },

    resolveDuplicatePregnancy: async (pregnancyReferenceId, keepPregnancyReferenceId, confirmation) => {
        const res = await fetch(
            `${config.api.url}/pregnancies/${pregnancyReferenceId}/resolve-duplicate`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ keepPregnancyReferenceId, confirmation })
            }
        );
        return requireClinicalSaveResult(await handleResponse(res));
    },

    // Visit APIs
    fetchVisits: async (pregnancyReferenceId) => {
        const local = await readClinicalResourceList({ resourceType: 'prenatalVisit', parentReferenceId: pregnancyReferenceId });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/prenatal-visits`, {
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    createVisit: async (pregnancyReferenceId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'prenatalVisit', parentReferenceId: pregnancyReferenceId, operation: 'create', payload: visitData });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/prenatal-visits`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleResponse(res), { requireId: true, resource: 'prenatal visit' });
    },

    updateVisit: async (pregnancyReferenceId, visitId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'prenatalVisit', parentReferenceId: pregnancyReferenceId, resourceReferenceId: visitId, operation: 'update', payload: visitData });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'prenatalVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/prenatal-visits/${visitId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleNoContentResponse(res));
    },

    voidPrenatalVisit: async (pregnancyReferenceId, visitId, voidReason) => {
        await assertClinicalChartReady({ resourceType: 'prenatalVisit', resourceReferenceId: visitId, parentReferenceId: pregnancyReferenceId });
        visitId = await clinicalNetworkRouteId({ resourceType: 'prenatalVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/prenatal-visits/${visitId}/void`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ voidReason })
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    // Postpartum Visit APIs
    fetchPostpartumVisits: async (pregnancyReferenceId) => {
        const local = await readClinicalResourceList({ resourceType: 'postpartumVisit', parentReferenceId: pregnancyReferenceId });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/postpartum-visits`, {
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    createPostpartumVisit: async (pregnancyReferenceId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'postpartumVisit', parentReferenceId: pregnancyReferenceId, operation: 'create', payload: visitData });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/postpartum-visits`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    updatePostpartumVisit: async (pregnancyReferenceId, visitId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'postpartumVisit', parentReferenceId: pregnancyReferenceId, resourceReferenceId: visitId, operation: 'update', payload: visitData });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'postpartumVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/postpartum-visits/${visitId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleNoContentResponse(res));
    },

    deletePostpartumVisit: async (pregnancyReferenceId, visitId) => {
        const local = await saveClinicalResource({ resourceType: 'postpartumVisit', parentReferenceId: pregnancyReferenceId, resourceReferenceId: visitId, operation: 'delete' });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'postpartumVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/postpartum-visits/${visitId}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleNoContentResponse(res));
    },

    voidPostpartumVisit: async (pregnancyReferenceId, visitId, voidReason) => {
        await assertClinicalChartReady({ resourceType: 'postpartumVisit', resourceReferenceId: visitId, parentReferenceId: pregnancyReferenceId });
        visitId = await clinicalNetworkRouteId({ resourceType: 'postpartumVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/postpartum-visits/${visitId}/void`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ voidReason })
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    // Newborn Visit APIs
    fetchNewbornVisits: async (pregnancyReferenceId) => {
        const local = await readClinicalResourceList({ resourceType: 'newbornVisit', parentReferenceId: pregnancyReferenceId });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/newborn-visits`, {
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    createNewbornVisit: async (pregnancyReferenceId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'newbornVisit', parentReferenceId: pregnancyReferenceId, operation: 'create', payload: visitData });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/newborn-visits`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    updateNewbornVisit: async (pregnancyReferenceId, visitId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'newbornVisit', parentReferenceId: pregnancyReferenceId, resourceReferenceId: visitId, operation: 'update', payload: visitData });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'newbornVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/newborn-visits/${visitId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleNoContentResponse(res));
    },

    deleteNewbornVisit: async (pregnancyReferenceId, visitId) => {
        const local = await saveClinicalResource({ resourceType: 'newbornVisit', parentReferenceId: pregnancyReferenceId, resourceReferenceId: visitId, operation: 'delete' });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'newbornVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/newborn-visits/${visitId}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleNoContentResponse(res));
    },

    voidNewbornVisit: async (pregnancyReferenceId, visitId, voidReason) => {
        await assertClinicalChartReady({ resourceType: 'newbornVisit', resourceReferenceId: visitId, parentReferenceId: pregnancyReferenceId });
        visitId = await clinicalNetworkRouteId({ resourceType: 'newbornVisit', parentReferenceId: pregnancyReferenceId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyReferenceId}/newborn-visits/${visitId}/void`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ voidReason })
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    // Lactation Visit APIs (uses pregnancy integer ID, not referenceId)
    fetchLactationVisits: async (pregnancyId) => {
        const local = await readClinicalResourceList({ resourceType: 'lactationVisit', parentReferenceId: pregnancyId });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/lactation-visits`, {
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    createLactationVisit: async (pregnancyId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'lactationVisit', parentReferenceId: pregnancyId, operation: 'create', payload: visitData });
        if (local) return local;
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/lactation-visits`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    updateLactationVisit: async (pregnancyId, visitId, visitData) => {
        const local = await saveClinicalResource({ resourceType: 'lactationVisit', parentReferenceId: pregnancyId, resourceReferenceId: visitId, operation: 'update', payload: visitData });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'lactationVisit', parentReferenceId: pregnancyId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/lactation-visits/${visitId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    deleteLactationVisit: async (pregnancyId, visitId) => {
        const local = await saveClinicalResource({ resourceType: 'lactationVisit', parentReferenceId: pregnancyId, resourceReferenceId: visitId, operation: 'delete' });
        if (local) return local;
        visitId = await clinicalNetworkRouteId({ resourceType: 'lactationVisit', parentReferenceId: pregnancyId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/lactation-visits/${visitId}`, {
            method: "DELETE",
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleNoContentResponse(res));
    },

    voidLactationVisit: async (pregnancyId, visitId, voidReason) => {
        await assertClinicalChartReady({ resourceType: 'lactationVisit', resourceReferenceId: visitId, parentReferenceId: pregnancyId });
        visitId = await clinicalNetworkRouteId({ resourceType: 'lactationVisit', parentReferenceId: pregnancyId }, visitId);
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/lactation-visits/${visitId}/void`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ voidReason })
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    // Baby APIs
    addBaby: async (referenceId, babyData) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/babies`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(babyData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    fetchBabies: async (referenceId, pregnancyReferenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/pregnancies/${pregnancyReferenceId}/babies`, {
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    fetchBirthEvent: async (referenceId, babyReferenceId) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/babies/${babyReferenceId}`, {
            headers: getAuthHeaders()
        });
        return requireClinicalSaveResult(await handleResponse(res));
    },

    updateBirthEvent: async (referenceId, babyReferenceId, birthEventData) => {
        const res = await fetch(`${config.api.url}/clients/${referenceId}/babies/${babyReferenceId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(birthEventData)
        });
        return requireClinicalSaveResult(await handleResponse(res));
    }
};

export default visitApi;
